import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Check } from 'lucide-react';
import Marquee from '../components/Marquee';
import Footer from '../components/Footer';
import SEO from '../components/SEO';

const groups = [
  {
    number: '01',
    title: 'Pre-Wedding',
    titleItalic: 'Services',
    items: [
      'Conduct a minimum of three (3) in-person or virtual planning meetings to review wedding details, including design and décor, vendor responsibilities, event timelines, and contract negotiations.',
      'Schedule and manage phone and email communications as mutually agreed upon.',
      'Develop a custom décor concept that aligns with the client\u2019s theme, vision, and event goals.',
      'Assist in identifying, evaluating, and securing the ideal venue.',
      'Review and negotiate pre-event and post-event contracts.',
      'Support the design, production, and printing of wedding stationery, including save-the-dates, invitations, welcome letters, programs, and menus.',
      'Research and secure hotel accommodations for guests.',
      'Oversee vendor deposit schedules and payment coordination.',
      'Negotiate group room blocks at conveniently located hotels for the couple and their guests.',
      'Coordinate transportation arrangements and vendor contracts for the couple and guests, when applicable.',
      'Create a comprehensive event timeline for vendors and the wedding party.',
      'Assist with menu planning and catering contract negotiations.',
      'Develop a personalized wedding checklist to ensure every detail is organized and accounted for.',
      'Coordinate site visits and vendor appointments, such as cake tastings and photography consultations, as scheduling permits.',
      'Provide a detailed budget along with regular monthly updates.',
      'Support vendor selection and negotiate terms with professional service providers.',
      'Prepare a master event schedule to guide the full wedding experience.',
      'Confirm all vendor contracts, logistics, and event timing prior to the wedding day.',
      'Direct the wedding rehearsal and coordinate the wedding party.',
      'Coordinate the ceremony processional and ensure the event begins on schedule.',
      'Manage event timing and communication with the family, wedding party, and vendors throughout the celebration.',
      'Provide additional services as mutually agreed upon by the client and company.',
    ],
  },
  {
    number: '02',
    title: 'On-Site Wedding Day',
    titleItalic: 'Services',
    items: [
      'Meet with the couple to review final wedding-day logistics, transportation, layout, and event arrangements.',
      'Provide guidance on wedding etiquette, ceremony flow, and event protocol.',
      'Conduct vendor follow-up communications to confirm deliveries, arrival times, and final arrangements.',
      'Serve as the on-site event producer to oversee execution and maintain operational flow.',
      'Coordinate with on-site entertainment and contracted talent.',
      'Oversee catering operations and beverage service throughout the event.',
      'Manage the day-of timeline to ensure seamless transitions between events.',
      'Provide two on-site wedding coordinators.',
      'Supervise event setup, including room layout, tablescapes, and décor placement.',
      'Execute and coordinate the rehearsal, ceremony, and reception from start to finish.',
      'Oversee venue breakdown and post-event teardown.',
    ],
  },
  {
    number: '03',
    title: 'Post-Wedding',
    titleItalic: 'Services',
    items: [
      'Provide post-event follow-up and client support, as needed.',
      'Conduct a formal post-event vendor evaluation and performance review.',
      'Prepare and deliver a final wedding summary report.',
      'Prepare and provide a final itemized invoice.',
    ],
  },
];

const ServiceTasks = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="bg-[#0a0a0a] min-h-screen text-white overflow-x-hidden">
      <SEO
        title="Service Tasks — Pre, On-Site & Post-Wedding Duties | Dion Magee Events"
        description="A detailed breakdown of every responsibility we handle — pre-wedding planning, on-site coordination, and post-wedding follow-through — by Sir Dion Magee Events & Weddings."
        path="/service-tasks"
        image="/og-image.jpg"
      />
      {/* HEADER */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#0a0a0a]/90 backdrop-blur-md border-b border-white/5">
        <div className="max-w-[1500px] mx-auto px-4 sm:px-6 lg:px-12 h-24 sm:h-24 md:h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 group min-w-0">
            <img
              src="/dm-logo.png"
              alt="Dion Magee Events & Weddings"
              className="w-[68px] h-[68px] md:w-[60px] md:h-[60px] lg:w-[68px] lg:h-[68px] object-contain flex-shrink-0"
            />
            <div className="leading-none min-w-0">
              <div className="text-white font-serif text-[24px] sm:text-[26px] md:text-[22px] lg:text-[26px] tracking-[0.1em] sm:tracking-[0.14em] md:tracking-[0.18em] leading-none whitespace-nowrap">
                DION MAGEE
              </div>
              <div className="text-[#c9a96e]/90 text-[11px] sm:text-[11px] md:text-[10px] lg:text-[11px] tracking-[0.2em] sm:tracking-[0.26em] md:tracking-[0.32em] mt-1.5 leading-none whitespace-nowrap">
                EVENTS &amp; WEDDINGS
              </div>
            </div>
          </Link>

          <div className="flex items-center gap-3 md:gap-6">
            <Link
              to="/wedding-planning"
              className="hidden sm:inline-flex items-center gap-2 text-white/80 hover:text-[#c9a96e] text-[11px] tracking-[0.3em] uppercase transition-colors"
            >
              <ArrowLeft size={14} /> Back To Packages
            </Link>
            <Link
              to="/#contact"
              className="text-[#c9a96e] hover:bg-[#c9a96e] hover:text-black px-5 md:px-7 py-3 border border-[#c9a96e]/60 text-[10px] md:text-[11px] tracking-[0.3em] uppercase transition-colors"
            >
              Enquire Now
            </Link>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="relative pt-44 pb-20 md:pt-52 md:pb-28 px-6 lg:px-12 overflow-hidden">
        {/* Ambient gold orbs */}
        <div
          className="absolute -top-32 -left-32 w-[55vw] h-[55vw] rounded-full opacity-20 blur-3xl pointer-events-none"
          style={{
            background:
              'radial-gradient(circle, rgba(232,213,168,0.55) 0%, transparent 70%)',
            animation: 'stOrbA 26s ease-in-out infinite',
            mixBlendMode: 'screen',
          }}
        />
        <div
          className="absolute -bottom-32 -right-20 w-[45vw] h-[45vw] rounded-full opacity-18 blur-3xl pointer-events-none"
          style={{
            background:
              'radial-gradient(circle, rgba(255,210,150,0.5) 0%, transparent 70%)',
            animation: 'stOrbB 32s ease-in-out infinite',
            mixBlendMode: 'screen',
          }}
        />

        <div className="relative z-10 max-w-[1500px] mx-auto text-center">
          <div className="flex items-center justify-center gap-4 mb-8">
            <span className="h-px w-12 md:w-16 bg-[#c9a96e]/60" />
            <p className="text-[#c9a96e] text-[11px] tracking-[0.5em] uppercase whitespace-nowrap">
              Service Details
            </p>
            <span className="h-px w-12 md:w-16 bg-[#c9a96e]/60" />
          </div>
          <h1 className="font-serif text-white text-5xl sm:text-6xl md:text-7xl lg:text-[100px] leading-[1.02] tracking-tight">
            Full Service
          </h1>
          <h1
            className="font-serif italic text-[#e8c98a] text-5xl sm:text-6xl md:text-7xl lg:text-[100px] leading-[1.02] tracking-tight -mt-1"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
          >
            Detailed Tasks
          </h1>
          <p className="mt-10 max-w-2xl mx-auto text-white/80 text-base md:text-lg leading-[1.9] font-light">
            A look behind the curtain — every detail our atelier handles when you entrust us with the Full Service Package. Tasks may include, but are not limited to, the following.
          </p>
        </div>
      </section>

      {/* Marquee */}
      <Marquee />

      {/* TASK GROUPS */}
      <section className="bg-[#0a0a0a] py-24 lg:py-32 px-6 lg:px-12">
        <div className="max-w-[1300px] mx-auto space-y-24 lg:space-y-32">
          {groups.map((g) => (
            <div key={g.number} className="grid lg:grid-cols-12 gap-10 lg:gap-16">
              {/* Left column: heading */}
              <div className="lg:col-span-4 lg:sticky lg:top-28 lg:self-start">
                <div className="font-serif text-[#c9a96e]/40 text-7xl md:text-8xl leading-none">
                  {g.number}
                </div>
                <div className="w-12 h-px bg-[#c9a96e] my-6" />
                <h2 className="font-serif text-white text-4xl md:text-5xl lg:text-[52px] leading-[1.05]">
                  {g.title}
                </h2>
                <h2
                  className="font-serif italic text-[#c9a96e] text-4xl md:text-5xl lg:text-[52px] leading-[1.05] -mt-1"
                  style={{ fontFamily: 'Cormorant Garamond, serif' }}
                >
                  {g.titleItalic}
                </h2>
                <p className="text-white/45 text-[10px] tracking-[0.4em] uppercase mt-6">
                  {g.items.length} curated tasks
                </p>
              </div>

              {/* Right column: tasks */}
              <div className="lg:col-span-8">
                <ul className="border-t border-white/10">
                  {g.items.map((task, idx) => (
                    <li
                      key={idx}
                      className="group flex items-start gap-5 py-5 border-b border-white/10 transition-colors hover:bg-white/[0.02]"
                    >
                      <div className="flex-shrink-0 w-9 h-9 mt-0.5 border border-[#c9a96e]/35 flex items-center justify-center group-hover:border-[#c9a96e] group-hover:bg-[#c9a96e]/10 transition-colors">
                        <Check size={14} className="text-[#c9a96e]" strokeWidth={2} />
                      </div>
                      <p className="text-white/80 text-[15px] leading-[1.85] font-light pt-1">
                        {task}
                      </p>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CLOSING CTA */}
      <section className="relative overflow-hidden bg-[#0a0a0a] py-28 lg:py-36 px-6 lg:px-12">
        <div
          className="absolute inset-0 opacity-25"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1515934751635-c81c6bc9a2d8?w=2000&q=80')",
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a] via-[#0a0a0a]/85 to-[#0a0a0a]" />
        <div className="relative z-10 max-w-3xl mx-auto text-center">
          <p className="text-[#c9a96e] text-[11px] tracking-[0.5em] uppercase mb-6">
            Ready to Begin
          </p>
          <h2 className="font-serif text-white text-4xl md:text-5xl lg:text-6xl leading-[1.1]">
            Entrust us with the
          </h2>
          <h2
            className="font-serif italic text-[#c9a96e] text-4xl md:text-5xl lg:text-6xl leading-[1.1] mt-1"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
          >
            details that matter
          </h2>
          <div className="mt-12 flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/#contact"
              className="inline-flex items-center justify-center gap-3 bg-[#e8d5a8] hover:bg-[#c9a96e] text-black px-10 py-4 text-[11px] tracking-[0.35em] uppercase transition-colors"
            >
              Start The Conversation
              <ArrowRight size={14} />
            </Link>
            <Link
              to="/wedding-planning"
              className="inline-flex items-center justify-center gap-3 border border-white/40 hover:border-[#c9a96e] hover:text-[#c9a96e] text-white px-10 py-4 text-[11px] tracking-[0.35em] uppercase transition-colors"
            >
              <ArrowLeft size={14} />
              Back To Packages
            </Link>
          </div>
        </div>
      </section>

      <Footer />

      <style>{`
        @keyframes stOrbA {
          0%, 100% { transform: translate(0, 0); }
          50%      { transform: translate(12%, 10%); }
        }
        @keyframes stOrbB {
          0%, 100% { transform: translate(0, 0); }
          50%      { transform: translate(-10%, -8%); }
        }
      `}</style>
    </div>
  );
};

export default ServiceTasks;
